import { addMonths, differenceInCalendarDays, getDaysInMonth } from 'date-fns'
import { fromZonedTime, toZonedTime } from 'date-fns-tz'

import { DateRange } from '@/models/helpers'
import { composeFormData } from '@/utils/api'

import { GUEST_CATEGORY_OPTIONS, MIN_VISIT_DURATION } from './add-visitor-modal.constants'
import {
  AccessTypeEnum,
  AddVisitorFormValues,
  PermissionTypeEnum,
  VisitorTypeEnum,
} from './add-visitor-modal.types'

export const getDefaultValues = (
  timezone?: string,
  defaultVisitorType?: AddVisitorFormValues['visitorType'],
  lockResidentId?: string | null,
) => {
  const now = timezone ? toZonedTime(new Date(), timezone) : new Date()
  return {
    accessType: AccessTypeEnum.enum['one-time'],
    coi: null,
    company: '',
    customAccess: false,
    dueDate: now,
    email: '',
    endOfDay: null,
    guestCategory: '',
    isNoteOpen: false,
    keyAccess: false,
    keylessAccess: false,
    monthlyPermission: '',
    monthlyRange: [null, null] as DateRange,
    name: '',
    note: '',
    permissionType: PermissionTypeEnum.enum.custom,
    phone: '',
    pinCode: false,
    resident: lockResidentId ?? null,
    selectedDays: [],
    startOfDay: null,
    timeEnd: now.getHours() * 60 + now.getMinutes() + 15,
    timeStart: now.getHours() * 60 + now.getMinutes(),
    visitorId: '',
    visitorType: defaultVisitorType ?? VisitorTypeEnum.enum.guest,
  }
}

const mergeDateAndTime = (datePart: Date, totalMinutes: number): Date => {
  const newDate = new Date(datePart)
  newDate.setHours(Math.floor(totalMinutes / 60), totalMinutes % 60, 0, 0)
  return newDate
}

// Note: One-time visitors have no date/time inputs, so their visit window is always
// "now" until MIN_VISIT_DURATION minutes from now, in the building's timezone.
export const getOneTimeVisitWindow = (buildingTimezone: string) => {
  const now = toZonedTime(new Date(), buildingTimezone)
  const timeStart = now.getHours() * 60 + now.getMinutes()

  return {
    dueDate: now,
    endOfDay: null,
    startOfDay: null,
    timeEnd: timeStart + MIN_VISIT_DURATION,
    timeStart,
  }
}

const getStartAndEndTime = (values: any, buildingTimezone: string) => {
  const { dueDate: zonedNow, timeStart: nowMinutes } = getOneTimeVisitWindow(buildingTimezone)

  const dueDate = values.dueDate ? new Date(values.dueDate) : zonedNow
  const timeStartMinutes: number = values.timeStart ?? nowMinutes
  let timeEndMinutes: number = values.timeEnd ?? timeStartMinutes + MIN_VISIT_DURATION

  // Note: Keyless Access and PIN code access use a separate start/end date pair (startOfDay,
  // endOfDay) instead of dueDate, since they need an explicit, possibly multi-day visit window.
  const isKeyIntegration =
    values.permissionType.id === 'custom' && (!!values.keyAccess || !!values.pinCode)

  const baseDate = isKeyIntegration && values.startOfDay ? new Date(values.startOfDay) : dueDate
  const endBaseDate = isKeyIntegration && values.endOfDay ? new Date(values.endOfDay) : baseDate
  const isSameDayWindow = differenceInCalendarDays(endBaseDate, baseDate) === 0

  if (isSameDayWindow && timeEndMinutes - timeStartMinutes < MIN_VISIT_DURATION) {
    timeEndMinutes = timeStartMinutes + MIN_VISIT_DURATION
  }

  const newTimeStart = fromZonedTime(
    mergeDateAndTime(baseDate, timeStartMinutes),
    buildingTimezone,
  ).toISOString()
  const newTimeEnd = fromZonedTime(
    mergeDateAndTime(endBaseDate, timeEndMinutes),
    buildingTimezone,
  ).toISOString()

  return { newTimeEnd, newTimeStart }
}

const WEEK_ORDINALS = ['first', 'second', 'third', 'fourth', 'fifth']
const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

const getWeekdayPattern = (date: Date): string => {
  const weekday = WEEKDAY_NAMES[date.getDay()]
  const weekOfMonth = WEEK_ORDINALS[Math.ceil(date.getDate() / 7) - 1]
  return `Every ${weekOfMonth} ${weekday} of the Month`
}

export const calculateMonthlyPattern = (range: DateRange): string => {
  const [rangeStart, rangeEnd] = range
  if (!rangeStart) return ''

  if (!rangeEnd || differenceInCalendarDays(rangeEnd, rangeStart) === 0) {
    return getWeekdayPattern(rangeStart)
  }

  return `${rangeStart.getDate()} day of the month - ${rangeEnd.getDate()} day of the month`
}

const RANGE_PATTERN_REGEX = /^(\d{1,2}) day of the month - (\d{1,2}) day of the month/
const WEEKDAY_PATTERN_REGEX = new RegExp(
  `^Every (${WEEK_ORDINALS.join('|')}) (${WEEKDAY_NAMES.join('|')}) of the Month$`,
  'i',
)

const withDayOfMonth = (reference: Date, dayOfMonth: number): Date => {
  const date = new Date(reference)
  date.setDate(1)
  date.setDate(Math.min(dayOfMonth, getDaysInMonth(date)))
  return date
}

const getNthWeekdayOfMonth = (
  reference: Date,
  weekday: number,
  occurrence: number,
): Date | null => {
  const date = new Date(reference)
  date.setDate(1)
  const dayOfMonth = 1 + ((weekday - date.getDay() + 7) % 7) + (occurrence - 1) * 7
  if (dayOfMonth > getDaysInMonth(date)) return null
  date.setDate(dayOfMonth)
  return date
}

export const parseMonthlyPattern = (pattern: string, reference: Date | null): DateRange => {
  const base = reference ?? new Date()

  const rangeMatch = pattern.match(RANGE_PATTERN_REGEX)
  if (rangeMatch) {
    const startDay = Number(rangeMatch[1])
    const endDay = Number(rangeMatch[2])
    return [
      withDayOfMonth(base, startDay),
      withDayOfMonth(endDay < startDay ? addMonths(base, 1) : base, endDay),
    ]
  }

  const weekdayMatch = pattern.match(WEEKDAY_PATTERN_REGEX)
  if (weekdayMatch) {
    const occurrence = WEEK_ORDINALS.indexOf(weekdayMatch[1].toLowerCase()) + 1
    const weekday = WEEKDAY_NAMES.findIndex(
      (name) => name.toLowerCase() === weekdayMatch[2].toLowerCase(),
    )
    const date = getNthWeekdayOfMonth(base, weekday, occurrence)
    return date ? [date, date] : [null, null]
  }

  return [null, null]
}

const visitorTypes = [
  { id: 'Friend', text: 'Friend' },
  { id: 'relative', text: 'Relative' },
  { id: 'personalTrainer', text: 'Personal Trainer' },
  { id: 'housekeeper', text: 'Housekeeper' },
  { id: 'handyman', text: 'Handyman' },
  { id: 'dogWalker', text: 'Dog Walker' },
  { id: 'other', text: 'Other' },
]

const permissionTypes = [
  { id: 'custom', text: 'One time' },
  { id: 'daily', text: 'Daily' },
  { id: 'weekly', text: 'Weekly' },
  { id: 'monthly', text: 'Monthly' },
  { id: 'always', text: 'Always' },
]

export const formatGuestDetailsToFormValues = (data: any, buildingTimezone?: string) => {
  const visitor = data?.visitor || {}
  const startZoned = visitor.startTime
    ? toZonedTime(new Date(visitor.startTime), buildingTimezone!)
    : null
  const endZoned = visitor.endTime
    ? toZonedTime(new Date(visitor.endTime), buildingTimezone!)
    : null
  const startTime = startZoned ? startZoned.getHours() * 60 + startZoned.getMinutes() : null
  const endTime = endZoned ? endZoned.getHours() * 60 + endZoned.getMinutes() : null
  const dueDate = startZoned
  const visitorType = visitorTypes.find(
    (type) =>
      type.id.toLowerCase() === `${visitor.type || ''}`.trim().toLowerCase() ||
      type.text.toLowerCase() === `${visitor.type || ''}`.trim().toLowerCase(),
  )
  const selectedDays = Array.isArray(visitor.periodicity)
    ? visitor.periodicity.map(Number).filter((day: number) => !Number.isNaN(day))
    : []

  return {
    coi: null,
    customAccess: !!visitor.customAccess,
    dueDate: dueDate,
    endOfDay: endZoned,
    isVisitorNoteOpen: !!visitor.notes,
    keyAccess: !!visitor.keyAccess,
    keylessAccess: !!visitor.keylessAccess,
    monthlyPermission: visitor.monthlyPermission || '',
    permissionType: permissionTypes.find((type) => type.id === visitor.mode) || permissionTypes[0],
    pinCode: !!visitor.pinCode,
    selectedDays,
    startOfDay: dueDate,
    timeEnd: endTime,
    timeStart: startTime,
    visitorCompany: visitor.company || '',
    visitorEmail: visitor.email || '',
    visitorId: `${visitor.guestId || ''}`,
    visitorName: visitor.name || '',
    visitorNote: visitor.notes || '',
    visitorPhone: visitor.phone ? String(visitor.phone) : '',
    visitorType: visitorType || { id: '', text: visitor.type || '' },
  }
}

export const formatValues = (values: AddVisitorFormValues) => ({
  clientId: values.resident ?? '',
  coi: values.coi,
  customAccess: values.customAccess,
  dueDate: values.dueDate,
  endDate:
    values.permissionType === PermissionTypeEnum.enum.monthly
      ? (values.monthlyRange?.[1] ?? null)
      : null,
  endOfDay: values.endOfDay,
  isVisitorNoteOpen: values.isNoteOpen,
  keyAccess: values.keyAccess,
  keylessAccess: values.keylessAccess,
  monthlyPermission: values.monthlyPermission,
  permissionType: { id: values.permissionType, text: values.permissionType },
  pinCode: values.pinCode,
  resident: values.resident,
  selectedDays: values.selectedDays,
  startOfDay: values.startOfDay,
  timeEnd: values.timeEnd,
  timeStart: values.timeStart,
  visitorCompany: values.company,
  visitorEmail: values.email,
  visitorId: values.visitorId,
  visitorName: values.name,
  visitorNote: values.note,
  visitorPhone: values.phone,
  visitorType: {
    id: values.guestCategory,
    text:
      GUEST_CATEGORY_OPTIONS.find((o) => o.value === values.guestCategory)?.label ??
      values.guestCategory,
  },
})

export const formatGuestFormData = (
  values: any,
  isRecurringType: boolean,
  buildingTimezone: string,
) => {
  const clientId = values.resident ?? null
  const { newTimeStart, newTimeEnd } = getStartAndEndTime(values, buildingTimezone)

  const formData = composeFormData({
    company: values.visitorCompany,
    customAccess: values.customAccess,
    email: values.visitorEmail,
    endTime: newTimeEnd,
    guestId: values.visitorId || undefined,
    keyAccess: values.keyAccess,
    keylessAccess: values.keylessAccess,
    mode: values.permissionType.id,
    monthlyPermission: isRecurringType ? values.monthlyPermission || 'Other' : undefined,
    name: values.visitorName,
    notes: values.isVisitorNoteOpen ? values.visitorNote : '',
    periodicity: values.selectedDays,
    phone: values.visitorPhone || undefined,
    pinCode: values.pinCode,
    startTime: newTimeStart,
    type: values.visitorType.text || 'Other',
  })

  return { clientId, formData }
}

export const formatVendorFormData = (
  values: any,
  isRecurringType: boolean,
  buildingId: number,
  buildingTimezone: string,
) => {
  const { newTimeStart, newTimeEnd } = getStartAndEndTime(values, buildingTimezone)

  return composeFormData({
    buildingId: String(buildingId),
    clientId: values.resident || undefined,
    coi: values.coi || undefined,
    company: values.visitorCompany,
    customAccess: values.customAccess,
    email: values.visitorEmail,
    endTime: newTimeEnd,
    keyAccess: values.keyAccess,
    keylessAccess: values.keylessAccess,
    mode: values.permissionType.id,
    monthlyPermission: isRecurringType ? values.monthlyPermission : undefined,
    name: values.visitorName,
    notes: values.isVisitorNoteOpen ? values.visitorNote : '',
    periodicity: values.selectedDays,
    phone: values.visitorPhone || undefined,
    pinCode: values.pinCode,
    startTime: newTimeStart,
    vendorId: values.visitorId || undefined,
  })
}
